import type { ReactNode } from "react"

import styles from "./CompanySmsSendingRuleRowEditV2.module.scss"

import {
	EncouragingRereservationUnitsSlider,
	formatUnitVisitCounts,
	getUnitSegmentColor,
} from "@/souhl_admin/components/features/companies/sms-sending-rule/EncouragingRereservationUnitsSlider/EncouragingRereservationUnitsSlider"

import type { useUnitsEdit } from "./useUnitsEdit"

type Props = {
	// 区間 [rangeStart, rangeEnd)
	rangeStart: number
	rangeEnd: number
	// useUnitsEdit の返り値をそのまま渡す
	unitsEdit: ReturnType<typeof useUnitsEdit>
	// useFieldArray の fields の id (unit 行の key)
	unitKeys: string[]
	// 「詳細設定」ボタンを押せなくする条件 (例: 選べる送付種別が無い)
	toggleDetailDisabled?: boolean
	// 詳細設定中 / 詳細設定前の説明文
	detailNote: string
	idleNote: string
	// 先頭 unit の開始の固定表示「開始 N 回 ({startFixedLabel}に固定)」
	startFixedLabel: string
	// unit ごとの設定 UI (送付種別 select / 転換 select など機能固有の部分)
	renderUnitControl: (unitIndex: number) => ReactNode
	// unit ごとのエラーメッセージ (無ければ undefined)
	getUnitErrorMessage: (unitIndex: number) => string | undefined
	// 配列全体の yup エラー (unit の input がマウントされていると .root に入る)
	rootErrorMessage: string | undefined
	// unit の色 (省略時は再予約案内の配色)
	getUnitColor?: (unitIndex: number) => string
}

// 来店回数区間を unit に分ける編集 UI の見た目。
// 再予約案内 unit と手動請求 unit で共通 (機能固有の select は renderUnitControl で注入する)
export const UnitsEdit = ({
	rangeStart,
	rangeEnd,
	unitsEdit,
	unitKeys,
	toggleDetailDisabled,
	detailNote,
	idleNote,
	startFixedLabel,
	renderUnitControl,
	getUnitErrorMessage,
	rootErrorMessage,
	getUnitColor = getUnitSegmentColor,
}: Props) => {
	const {
		isDetailEnabled,
		unitMinVisitCounts,
		unitSegments,
		unitBoundaries,
		getLimits,
		handleUnitMinVisitCountInput,
		handleUnitBoundariesChange,
		toggleDetail,
		splittableSegment,
		addUnit,
		removeUnit,
	} = unitsEdit

	return (
		<div className={styles.units_container}>
			<div className={styles.units_header}>
				<button
					type="button"
					className={styles.unit_detail_button}
					disabled={!isDetailEnabled && toggleDetailDisabled}
					onClick={toggleDetail}
				>
					{isDetailEnabled ? "詳細設定を解除" : "詳細設定"}
				</button>
				<span className={styles.units_note}>
					{isDetailEnabled ? detailNote : idleNote}
				</span>
			</div>
			{isDetailEnabled && (
				<>
					<EncouragingRereservationUnitsSlider
						rangeStart={rangeStart}
						rangeEnd={rangeEnd}
						boundaries={unitBoundaries}
						onBoundariesChange={handleUnitBoundariesChange}
						getSegmentColor={getUnitColor}
					/>
					<div className={styles.unit_rows}>
						{unitKeys.map((unitKey, unitIndex) => {
							const segment = unitSegments[unitIndex]
							if (segment === undefined) return null
							const unitErrorMessage = getUnitErrorMessage(unitIndex)
							return (
								<div key={unitKey} className={styles.unit_row}>
									<div
										className={styles.unit_color_box}
										style={{
											backgroundColor: getUnitColor(unitIndex),
										}}
									/>
									<span className={styles.unit_label}>
										{formatUnitVisitCounts(segment)}
									</span>
									{unitIndex === 0 ? (
										<span className={styles.unit_start_fixed}>
											開始 {rangeStart} 回 ({startFixedLabel}に固定)
										</span>
									) : (
										<span className={styles.unit_start_input}>
											開始
											<input
												type="number"
												min={getLimits(unitIndex).min}
												max={getLimits(unitIndex).max}
												className={styles.unit_visit_count_input}
												value={unitMinVisitCounts[unitIndex] ?? ""}
												onChange={(e) =>
													handleUnitMinVisitCountInput(
														unitIndex,
														e.target.value,
														false
													)
												}
												onBlur={(e) =>
													handleUnitMinVisitCountInput(
														unitIndex,
														e.target.value,
														true
													)
												}
												onKeyDown={(e) => {
													if (e.key === "Enter") {
														e.preventDefault()
														e.currentTarget.blur()
													}
												}}
											/>
											回〜
										</span>
									)}
									{renderUnitControl(unitIndex)}
									<button
										type="button"
										className={styles.unit_remove_button}
										title="この区間を削除"
										onClick={() => removeUnit(unitIndex)}
									>
										✕
									</button>
									{unitErrorMessage && (
										<small role="alert" className={styles.error_message}>
											{unitErrorMessage}
										</small>
									)}
								</div>
							)
						})}
					</div>
					<button
						type="button"
						className={styles.unit_add_button}
						disabled={splittableSegment === null}
						title={
							splittableSegment === null
								? "これ以上区間を分けられません"
								: undefined
						}
						onClick={addUnit}
					>
						＋区間を追加
					</button>
					{rootErrorMessage && (
						<small role="alert" className={styles.error_message}>
							{rootErrorMessage}
						</small>
					)}
				</>
			)}
		</div>
	)
}
